import { Button } from '@finn/design-system/atoms/button';
import { Input } from '@finn/design-system/atoms/input';
import { showToast } from '@finn/design-system/atoms/toast';
import { Recaptcha, recaptchaCall } from '@finn/ui-components';
import { EmailCollectionFormMetadata } from '@finn/ui-cosmic';
import { parseToHtml, useIntl } from '@finn/ui-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  FormInputs,
  initialValues,
  validationSchema,
} from '~/components/FormElements/EmailInput/config';
import { callWebhookFromId } from '~/services/webhook';
import { loyaltyVoucherRequested } from '~/ui-modules/EmailCollectionForm/tracking';

type Props = {
  data: EmailCollectionFormMetadata;
};

const EmailCollectionForm: React.FC<Props> = ({ data }) => {
  const [responseStatus, setResponseStatus] = useState(null);
  const descriptionHtml = parseToHtml(data.description);
  const i18n = useIntl();
  const form = useForm<FormInputs>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });
  const submitHandler = useCallback(async () => {
    const formData = form.getValues();
    const token = await recaptchaCall();
    let response;
    try {
      response = await callWebhookFromId(data?.frontend_id_for_webhook, {
        ...formData,
        token,
      });
      loyaltyVoucherRequested({ location: data?.title });
      setResponseStatus(response?.status);
    } catch {
      setResponseStatus(500);
    } finally {
      setTimeout(setResponseStatus, 5000);
    }
  }, [form.getValues, data]);

  useEffect(() => {
    if (responseStatus) {
      showToast({
        status: responseStatus === 200 ? 'success' : 'error',
        title:
          responseStatus === 200 ? data.success_message : data.error_message,
      });
    }
  }, [data.error_message, data.success_message, responseStatus]);

  return (
    <>
      <div className="container grid grid-cols-1 md:grid-cols-2">
        <div className="md:grid md:grid-cols-[5fr,1fr]">
          <h3 className="mb-6 mt-2 mobile-t3-semibold md:web-t3-semibold">
            {data?.title}
          </h3>
        </div>
        <div>
          <div className="mb-6 body-16-light">{descriptionHtml}</div>
          <form onSubmit={form.handleSubmit(submitHandler)} className="mb-2">
            <div className="grid grid-cols-1 items-start sm:grid-cols-[1.5fr,1fr]">
              <div className="mb-4 sm:mb-0">
                <Controller
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <Input
                      label={data?.email_placeholder_text || 'Email Address'}
                      error={
                        !!fieldState.error &&
                        fieldState.isTouched &&
                        i18n.formatMessage(fieldState.error.message)
                      }
                      {...field}
                    />
                  )}
                />
              </div>
              <Button type="submit" loading={form.formState.isSubmitting}>
                {data?.submit_button_text || 'Submit'}
              </Button>
            </div>
          </form>
          <Recaptcha />
        </div>
      </div>
    </>
  );
};
export default EmailCollectionForm;
